import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router";
import { getBusiness } from "../../api/business.api";
import { getServices } from "../../api/service.api";
import { getAvailableSlots, createBooking } from "../../api/booking.api";
import { ServiceCard } from "../../components/booking/ServiceCard";
import { TimeSlotGrid } from "../../components/booking/TimeSlotGrid";
import { BookingSummary } from "../../components/booking/BookingSummary";
import { useAuthStore } from "../../stores/auth.store";
import type { BusinessProfile, Service, TimeSlot } from "@abejon/shared";

type Step = "service" | "slot" | "guest" | "confirm";

function timeToMinutes(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

function minutesToTime(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
}

export function BookingPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  // Data
  const [business, setBusiness] = useState<BusinessProfile | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [slots, setSlots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [error, setError] = useState("");

  // Booking state
  const [step, setStep] = useState<Step>("service");
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  });
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState<{
    id: string;
    lookupCode: string | null;
  } | null>(null);

  // Load business + services
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;

    async function load() {
      try {
        const data = await getBusiness(slug!);
        if (cancelled) return;
        setBusiness(data.business);

        const servicesData = await getServices(data.business.id);
        if (!cancelled) setServices(servicesData.services);
      } catch {
        if (!cancelled) setError("Business not found");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [slug]);

  // Load slots when date or service changes
  useEffect(() => {
    if (!business || !selectedService || !selectedDate) return;
    let cancelled = false;

    async function loadSlots() {
      setSlotsLoading(true);
      setSelectedSlot(null);
      try {
        const data = await getAvailableSlots(business!.id, selectedDate, selectedService!.id);
        if (!cancelled) setSlots(data.slots);
      } catch {
        if (!cancelled) setSlots([]);
      } finally {
        if (!cancelled) setSlotsLoading(false);
      }
    }
    loadSlots();
    return () => { cancelled = true; };
  }, [business, selectedService, selectedDate]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-gray-900" />
      </div>
    );
  }

  if (error || !business) {
    return (
      <div className="py-16 text-center">
        <h1 className="text-2xl font-bold text-gray-300">Business not found</h1>
        <Link to="/explore" className="mt-4 inline-block text-sm font-medium text-gray-900 underline">
          Back to explore
        </Link>
      </div>
    );
  }

  function handleSelectService(service: Service) {
    setSelectedService(service);
    setStep("slot");
  }

  function handleSelectSlot(slot: TimeSlot) {
    setSelectedSlot(slot);
  }

  function handleSlotContinue() {
    if (!selectedSlot) return;
    // If user is logged in, skip guest info
    if (user) {
      setStep("confirm");
    } else {
      setStep("guest");
    }
  }

  function handleGuestContinue(e: React.FormEvent) {
    e.preventDefault();
    setStep("confirm");
  }

  async function handleConfirm() {
    if (!business || !selectedService || !selectedSlot) return;
    setSubmitting(true);
    setError("");

    try {
      const { booking } = await createBooking({
        businessId: business.id,
        serviceId: selectedService.id,
        date: selectedDate,
        startTime: selectedSlot.startTime,
        ...(user
          ? {}
          : {
              guestName,
              guestEmail,
              guestPhone,
            }),
        notes: notes || undefined,
      });

      setBookingResult({
        id: booking.id,
        lookupCode: booking.lookupCode,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create booking");
    } finally {
      setSubmitting(false);
    }
  }

  // Success state
  if (bookingResult) {
    return (
      <div className="mx-auto max-w-md py-12 text-center">
        <div className="mb-4 text-4xl">&#10003;</div>
        <h1 className="text-2xl font-bold text-gray-900">Booking Confirmed!</h1>
        <p className="mt-2 text-sm text-gray-500">
          Your appointment has been booked.
        </p>

        {bookingResult.lookupCode && (
          <div className="mt-4 rounded-lg bg-gray-50 p-4">
            <p className="text-sm text-gray-500">Your lookup code:</p>
            <p className="mt-1 text-2xl font-bold tracking-widest">{bookingResult.lookupCode}</p>
            <p className="mt-1 text-xs text-gray-400">
              Save this code to view or manage your booking later.
            </p>
          </div>
        )}

        <div className="mt-6 flex justify-center gap-3">
          <Link
            to={`/b/${business.slug}`}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Back to Profile
          </Link>
          <Link
            to="/"
            className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
          >
            Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md py-8">
      {/* Header */}
      <div className="mb-6">
        <Link to={`/b/${business.slug}`} className="text-sm text-gray-500 hover:text-gray-700">
          &larr; {business.displayName}
        </Link>
        <h1 className="mt-1 text-2xl font-bold">Book Appointment</h1>
      </div>

      {/* Step indicator */}
      <div className="mb-6 flex gap-1">
        {(["service", "slot", ...(user ? [] : ["guest"]), "confirm"] as Step[]).map((s, i) => (
          <div
            key={s}
            className={`h-1 flex-1 rounded-full ${
              (["service", "slot", "guest", "confirm"] as Step[]).indexOf(step) >=
              (["service", "slot", "guest", "confirm"] as Step[]).indexOf(s)
                ? "bg-gray-900"
                : "bg-gray-200"
            }`}
          />
        ))}
      </div>

      {error && (
        <p className="mb-4 text-sm text-red-600">{error}</p>
      )}

      {/* Step 1: Select Service */}
      {step === "service" && (
        <div>
          <h2 className="mb-3 text-lg font-semibold">Select a Service</h2>
          {services.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-400">
              No services available.
            </p>
          ) : (
            <div className="space-y-3">
              {services.map((s) => (
                <button
                  key={s.id}
                  onClick={() => handleSelectService(s)}
                  className="w-full text-left"
                >
                  <ServiceCard service={s} />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 2: Select Date & Time */}
      {step === "slot" && selectedService && (
        <div>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold">Select Date & Time</h2>
            <button onClick={() => setStep("service")} className="text-sm text-gray-500 hover:text-gray-700">
              Change service
            </button>
          </div>

          <p className="mb-3 text-sm text-gray-500">
            {selectedService.name} &middot; {selectedService.durationMinutes} min
          </p>

          <div className="mb-4">
            <label className="mb-1 block text-sm font-medium text-gray-700">Date</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              min={new Date().toISOString().split("T")[0]}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
            />
          </div>

          <TimeSlotGrid
            slots={slots}
            selectedSlot={selectedSlot}
            onSelect={handleSelectSlot}
            loading={slotsLoading}
          />

          {selectedSlot && (
            <button
              onClick={handleSlotContinue}
              className="mt-4 w-full rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800"
            >
              Continue
            </button>
          )}
        </div>
      )}

      {/* Step 3: Guest Info (only for unauthenticated users) */}
      {step === "guest" && (
        <div>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold">Your Information</h2>
            <button onClick={() => setStep("slot")} className="text-sm text-gray-500 hover:text-gray-700">
              Change time
            </button>
          </div>

          <form onSubmit={handleGuestContinue} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Name</label>
              <input
                type="text"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                required
                maxLength={100}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={guestEmail}
                onChange={(e) => setGuestEmail(e.target.value)}
                required
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Phone</label>
              <input
                type="tel"
                value={guestPhone}
                onChange={(e) => setGuestPhone(e.target.value)}
                required
                maxLength={20}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Notes (optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                maxLength={500}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
              />
            </div>
            <button
              type="submit"
              className="w-full rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800"
            >
              Review Booking
            </button>
          </form>
        </div>
      )}

      {/* Step 4: Confirm */}
      {step === "confirm" && selectedService && selectedSlot && (
        <div>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold">Confirm Booking</h2>
            <button
              onClick={() => setStep(user ? "slot" : "guest")}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Go back
            </button>
          </div>

          <BookingSummary
            businessName={business.displayName}
            serviceName={selectedService.name}
            date={selectedDate}
            startTime={selectedSlot.startTime}
            endTime={selectedSlot.endTime}
            durationMinutes={selectedService.durationMinutes}
            priceInCents={selectedService.priceInCents}
          />

          {!user && (guestName || guestEmail) && (
            <div className="mt-4 rounded-lg border border-gray-200 p-4">
              <h3 className="mb-2 text-sm font-semibold text-gray-500 uppercase">Contact Info</h3>
              <div className="space-y-1 text-sm">
                <p><span className="text-gray-500">Name:</span> {guestName}</p>
                <p><span className="text-gray-500">Email:</span> {guestEmail}</p>
                <p><span className="text-gray-500">Phone:</span> {guestPhone}</p>
              </div>
            </div>
          )}

          {notes && (
            <div className="mt-4 rounded-lg border border-gray-200 p-4">
              <h3 className="mb-1 text-sm font-semibold text-gray-500 uppercase">Notes</h3>
              <p className="text-sm">{notes}</p>
            </div>
          )}

          <button
            onClick={handleConfirm}
            disabled={submitting}
            className="mt-6 w-full rounded-lg bg-gray-900 px-4 py-3 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
          >
            {submitting ? "Booking..." : "Confirm Booking"}
          </button>

          <p className="mt-3 text-center text-xs text-gray-400">
            Payment is collected in person.
          </p>
        </div>
      )}
    </div>
  );
}
